import { IconProp } from "@fortawesome/fontawesome-svg-core";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { DisabledOverlay, KeyboardShortcutToolTipComponent, SimpleTooltip } from "@iventis/components";
import { InteractiveElement } from "@iventis/styles/src/components";
import { useIventisTranslate } from "@iventis/translations/use-iventis-translate";
import { Content } from "@iventis/translations/content/typed-content";
import React, { FunctionComponent, ReactElement } from "react";
import { styled } from "@iventis/styles";

interface GenericToolBarButtonProps {
    action: () => void;
    selected?: boolean;
    colour: string;
    hoverColour: string;
    hoverSelectedColour?: string;
    disabled: boolean;
    selectedColour: string;
    disabledColour: string;
    buttonWidth: string;
    buttonHeight: string;
    padding?: string;
    className?: string;
    dataCy?: string;
    selectedBackgroundColour?: string;
    tooltipHeader: string;
    tooltipShortcut?: string;
}

export const ToolBarButton: FunctionComponent<{ icon: IconProp; text: string; spin?: boolean; children?: ReactElement } & GenericToolBarButtonProps> = ({
    icon,
    action,
    selected = false,
    colour,
    hoverColour,
    hoverSelectedColour,
    disabled,
    disabledColour,
    selectedColour,
    buttonWidth,
    buttonHeight,
    padding,
    spin = false,
    text,
    className,
    children,
    dataCy,
    selectedBackgroundColour,
    tooltipHeader,
    tooltipShortcut,
}) => (
    // Given we are allowing tooltips even when disabled, we use a disabled overlay instead of setting the button to disabled
    // so that keyboard users can also see the tooltips by focusing on them (via tabbing)
    <SimpleTooltip text={<KeyboardShortcutToolTipComponent header={tooltipHeader} shortcut={tooltipShortcut} />} placement="bottom">
        <StyledToolbarButtonContainer className={className} data-cy={`toolbar-button-container-${dataCy}`}>
            {disabled && <DisabledOverlay opacity={0.3} />}
            <StyledIconHighlightButton
                onClick={() => !disabled && !spin && action()}
                data-cy={`toolbar-button-${dataCy}`}
                selected={selected}
                colour={colour}
                disabled={false}
                hoverColour={hoverColour}
                hoverSelectedColour={hoverSelectedColour}
                disabledColour={disabledColour}
                selectedColour={selectedColour}
                buttonWidth={buttonWidth}
                buttonHeight={buttonHeight}
                padding={padding}
                className={className}
                selectedBackgroundColour={selectedBackgroundColour}
            >
                <span className="hidden">{text}</span>
                <FontAwesomeIcon spin={spin} size="lg" icon={icon} color={chooseColourIcon(selected, disabled, colour, selectedColour, disabledColour)} />
                {children}
            </StyledIconHighlightButton>
        </StyledToolbarButtonContainer>
    </SimpleTooltip>
);

export const CommentsButton: FunctionComponent<GenericToolBarButtonProps> = ({
    action,
    selected,
    disabled,
    colour,
    hoverColour,
    hoverSelectedColour,
    selectedColour,
    disabledColour,
    buttonWidth,
    buttonHeight,
    padding,
    selectedBackgroundColour,
    tooltipHeader,
    tooltipShortcut,
}) => (
    <ToolBarButton
        action={action}
        icon={["far", "message-plus"]}
        selected={selected}
        colour={colour}
        dataCy="comments-mode"
        hoverColour={hoverColour}
        hoverSelectedColour={hoverSelectedColour}
        disabled={disabled}
        selectedColour={selectedColour}
        disabledColour={disabledColour}
        selectedBackgroundColour={selectedBackgroundColour}
        buttonWidth={buttonWidth}
        buttonHeight={buttonHeight}
        padding={padding}
        text={tooltipHeader}
        tooltipHeader={tooltipHeader}
        tooltipShortcut={tooltipShortcut}
    />
);

export const CommentsVisibilityButton: FunctionComponent<GenericToolBarButtonProps> = ({
    action,
    selected,
    disabled,
    colour,
    hoverColour,
    hoverSelectedColour,
    selectedColour,
    disabledColour,
    buttonWidth,
    buttonHeight,
    padding,
    tooltipHeader,
}) => (
    <ToolBarButton
        action={action}
        icon={["far", selected ? "eye" : "eye-slash"]}
        selected={selected}
        colour={colour}
        dataCy="comments-visibility"
        hoverColour={hoverColour}
        disabled={disabled}
        selectedColour={selectedColour}
        disabledColour={disabledColour}
        buttonWidth={buttonWidth}
        buttonHeight={buttonHeight}
        hoverSelectedColour={hoverSelectedColour}
        padding={padding}
        text={tooltipHeader}
        tooltipHeader={tooltipHeader}
    />
);

export const UndoButton: FunctionComponent<GenericToolBarButtonProps> = ({
    action,
    selected,
    disabled,
    colour,
    hoverColour,
    selectedColour,
    disabledColour,
    buttonWidth,
    buttonHeight,
    padding,
    tooltipHeader,
    tooltipShortcut,
}) => (
    <ToolBarButton
        action={action}
        icon={["far", "rotate-left"]}
        selected={selected}
        colour={colour}
        dataCy="undo"
        hoverColour={hoverColour}
        disabled={disabled}
        selectedColour={selectedColour}
        disabledColour={disabledColour}
        buttonWidth={buttonWidth}
        buttonHeight={buttonHeight}
        padding={padding}
        text={tooltipHeader}
        tooltipHeader={tooltipHeader}
        tooltipShortcut={tooltipShortcut}
    />
);

export const RedoButton: FunctionComponent<GenericToolBarButtonProps> = ({
    action,
    selected,
    disabled,
    colour,
    hoverColour,
    selectedColour,
    disabledColour,
    buttonWidth,
    buttonHeight,
    padding,
    tooltipHeader,
    tooltipShortcut,
}) => (
    <ToolBarButton
        action={action}
        icon={["far", "rotate-right"]}
        selected={selected}
        colour={colour}
        dataCy="redo"
        hoverColour={hoverColour}
        disabled={disabled}
        selectedColour={selectedColour}
        disabledColour={disabledColour}
        buttonWidth={buttonWidth}
        buttonHeight={buttonHeight}
        padding={padding}
        text={tooltipHeader}
        tooltipHeader={tooltipHeader}
        tooltipShortcut={tooltipShortcut}
    />
);

export const ConfirmAction: FunctionComponent<GenericToolBarButtonProps> = ({
    action,
    selected,
    disabled,
    colour,
    hoverColour,
    selectedColour,
    disabledColour,
    buttonWidth,
    buttonHeight,
    selectedBackgroundColour,
    tooltipHeader,
    tooltipShortcut,
}) => {
    const translate = useIventisTranslate();
    return (
        <ToolBarButton
            action={action}
            icon={["far", "check"]}
            selected={selected}
            colour={colour}
            hoverColour={hoverColour}
            disabled={disabled}
            selectedColour={selectedColour}
            disabledColour={disabledColour}
            buttonWidth={buttonWidth}
            buttonHeight={buttonHeight}
            text={translate(Content.map4.toolbarButtonTooltips.confirm_action)}
            selectedBackgroundColour={selectedBackgroundColour}
            dataCy="confirm-drawing"
            tooltipHeader={tooltipHeader}
            tooltipShortcut={tooltipShortcut}
        />
    );
};

export const CopyMapObjects: FunctionComponent<GenericToolBarButtonProps> = ({
    action,
    selected,
    disabled,
    colour,
    hoverColour,
    selectedColour,
    disabledColour,
    buttonWidth,
    buttonHeight,
    padding,
    dataCy,
    tooltipHeader,
    tooltipShortcut,
}) => (
    <ToolBarButton
        action={action}
        icon={["far", "copy"]}
        selected={selected}
        colour={colour}
        hoverColour={hoverColour}
        disabled={disabled}
        selectedColour={selectedColour}
        disabledColour={disabledColour}
        buttonWidth={buttonWidth}
        buttonHeight={buttonHeight}
        padding={padding}
        text={tooltipHeader}
        dataCy={dataCy}
        tooltipHeader={tooltipHeader}
        tooltipShortcut={tooltipShortcut}
    />
);

const StyledToolbarButtonContainer = styled.span`
    position: relative;
    display: flex;
    align-items: center;
`;

export const StyledIconHighlightButton = styled(InteractiveElement)<{
    selected: boolean;
    colour: string;
    hoverColour: string;
    hoverSelectedColour: string;
    disabled: boolean;
    selectedColour: string;
    disabledColour: string;
    buttonWidth: string;
    buttonHeight: string;
    padding: string;
    selectedBackgroundColour?: string;
}>`
    position: relative;
    z-index: 200;
    width: ${(props) => props.buttonWidth};
    height: ${(props) => props.buttonHeight};
    margin-inline: 4px;
    display: flex;
    justify-content: center;
    align-items: center;
    border-radius: 4px;
    cursor: ${(props) => (props.disabled ? "default" : "pointer")};
    color: ${(props) => chooseColourIcon(props.selected, props.disabled, props.colour, props.selectedColour, props.disabledColour)};
    transition: 150ms ease-in-out;
    padding: ${(props) => props.padding};
    :hover {
        background-color: ${(props) => props.hoverColour};
        svg {
            transition: 150ms ease-in-out;
            color: ${(props) => props.hoverSelectedColour};
        }
    }
    ${(props) => (props.selectedBackgroundColour && props.selected ? `background-color:${props.selectedBackgroundColour}` : "")};
`;

const chooseColourIcon = (selected: boolean, disabled: boolean, normalColour: string, selectedColour: string, disabledColour: string): string => {
    if (disabled) {
        return disabledColour;
    }

    if (selected) {
        return selectedColour;
    }

    return normalColour;
};
